import React, { useState, useRef } from "react";
import { saveCroppedImage } from "../../Library/Storage/StorageAdapter";
import { SidebarBtn } from "./Utilities/SidebarBtn";
import { IconLibrary, IconEditor, IconCrop, IconTick, IconCross, IconAdd, IconTextBox, IconLink, IconPrevious, IconNext, IconSubmit, IconMathBoxPi, IconMcqOption, IconDiscard } from "./Utilities/Icons";
import { useQuestions } from "../QuestionStore";
import { useNavigate } from "react-router-dom";
import { questions_txt } from "../../Test/dummy";


/**
 * WorkspaceShell — thin left icon bar + full content area.
 *
 * Props:
 *   LibraryComponent   : React component for the media library
 *   ViewerComponent    : React component for the media viewer
 *
 * The shell tracks the last selected media item from Library.
 * Clicking the editor icon shows the viewer with that item.
 * Tick and cross buttons are placeholders — no implementation yet.
 *
 * Usage:
 *   <WorkspaceShell
 *     LibraryComponent={Library}
 *     ViewerComponent={MediaViewer}
 *   />
 */

// items: {
//     ind: null,
//     media: null,
//     res : {},

// }
// const data_schema = {
//     items: [],
//     solution:{
//         mcq: false,
//         options: [],
//         hint: ""
//     }
// }


// ─── MAIN SHELL ──────────────────────────────────────────────
export default function WorkspaceShell({ LibraryComponent, ViewerComponent }) {
    const navigate = useNavigate();

    const [activePanel, setActivePanel] = useState("library"); // "library" | "editor"
    const [lastSelectedItem, setLastSelectedItem] = useState(null);
    const viewerRef = useRef(null);
    const [activeAction, setActiveAction] = useState(null); // "crop" | "paint" | "textbox" | null
    const [mcqMode, setMcqMode] = useState(false);
    const mcqCounter = useRef(1);
    const { addItemToCurrentQuestion, getCurrentQuestion, addQuestion, nextQuestion, prevQuestion, getAllData, discardDraft } = useQuestions();

    const handleConfirm = () => {
        console.log("log1", lastSelectedItem?.type)
        if (activeAction === "crop" && viewerRef.current) {
            viewerRef.current.confirmCrop();  // triggers cropMedia → onCropDone
            setActiveAction(null);
        } else if (activeAction === null && activePanel === "editor" && lastSelectedItem?.type !== "pdf") {

            if (lastSelectedItem?.type === "image") {
                const dataUrl = viewerRef.current?.getExportedDataUrl();
                if (dataUrl) {
                    addItem({ media: "image", res: { url: dataUrl, local: true } });
                }
            } else {
                addItem({ media: lastSelectedItem?.type, res: { url: lastSelectedItem?.dataUrl, local: true } });

            }

        }

    };
    const addItem = (payload) => {
        if (mcqMode) {
            payload.res.mcq = mcqCounter.current++;
        }
        addItemToCurrentQuestion(payload);
    };

    const handleCancel = () => {
        if (activeAction === "crop" && viewerRef.current) {
            viewerRef.current.cancelCrop();
        }
        setActiveAction(null);
    };

    const handleStartCrop = () => {
        if (activePanel !== "editor" || !viewerRef.current) return;
        setActiveAction("crop");
        viewerRef.current.startCrop();
    };


    // Called by Library when user selects a media item
    const handleItemSelected = (item) => {
        setLastSelectedItem(item);
        setActivePanel("editor")
    };
    const handleSubmit = () => {
        const payload = getAllData();
        navigate('/preview', {
            state: {
                rawQuestions: payload.questions
            }
        });
    };

    return (
        <div style={styles.root}>
            {/* ── Left icon bar ── */}
            <div style={styles.sidebar}>
                {/* Top group */}
                <div style={styles.sidebarGroup}>
                    <SidebarBtn
                        icon={<IconLibrary />}
                        active={activePanel === "library"}
                        onClick={() => setActivePanel("library")}
                        tooltip="Library"
                    />
                    <SidebarBtn
                        icon={<IconEditor />}
                        active={activePanel === "editor"}
                        onClick={() => setActivePanel("editor")}
                        tooltip="Editor"
                    />
                    <SidebarBtn
                        icon={<IconCrop />}
                        active={true}
                        onClick={handleStartCrop}
                        tooltip="Crop"
                    />
                    <SidebarBtn
                        icon={<IconTextBox />}
                        active={false}
                        onClick={() => {
                            addItem({
                                media: "text",
                                res: {
                                    text: "",
                                    size: "26px"
                                }
                            })
                        }}
                        tooltip="Add Text box"
                    />
                    <SidebarBtn
                        icon={<IconMathBoxPi />}
                        active={false}
                        onClick={() => {
                            addItem({
                                media: "text",
                                res: {
                                    text: "",
                                    size: "26px",
                                    math: true,
                                }
                            })
                        }}
                        tooltip="Add Math box"
                    />
                    <SidebarBtn
                        icon={<IconLink />}
                        active={false}
                        onClick={() => {
                            addItem({
                                media: "link",
                                res: {
                                    url: "",
                                    local: false
                                }
                            })
                        }}
                        tooltip="Attach Link"
                    />
                    <SidebarBtn
                        icon={<IconMcqOption />}
                        active={mcqMode}           // lights up when on
                        onClick={() => {
                            setMcqMode(prev => {
                                if (prev) mcqCounter.current = 1;  // reset counter on turn off
                                return !prev;
                            });
                        }}
                        tooltip="Option Mode"
                    />
                    <SidebarBtn
                        icon={<IconDiscard />}
                        active={false}
                        onClick={() => {
                            discardDraft();
                        }}
                        tooltip="Discard Draft"
                    />
                </div>

                {/* Bottom group — tick & cross */}
                <div style={styles.sidebarGroup}>
                    <SidebarBtn
                        icon={<IconTick />}
                        active={false}
                        onClick={handleConfirm}
                        tooltip="Confirm"
                    />
                    <SidebarBtn
                        icon={<IconCross />}
                        active={false}
                        onClick={handleCancel}
                        tooltip="Cancel"
                    />

                    <SidebarBtn
                        icon={<IconPrevious />}
                        active={false}
                        onClick={() => { prevQuestion(); }}
                        tooltip="Previous"
                    />
                    <SidebarBtn
                        icon={<IconAdd />}
                        active={false}
                        onClick={() => { addQuestion(); }}
                        tooltip="Add new question."
                    />
                    <SidebarBtn
                        icon={<IconNext />}
                        active={false}
                        onClick={() => { nextQuestion(); }}
                        tooltip="Previous"
                    />
                    <SidebarBtn
                        icon={<IconSubmit />}
                        active={false}
                        onClick={handleSubmit}
                        tooltip="Submit Quiz"
                    />
                </div>
            </div>

            {/* ── Content area ── */}
            <div style={styles.content}>
                {activePanel === "library" && (
                    <LibraryComponent onItemSelected={handleItemSelected} />
                )}
                {activePanel === "editor" && (

                    <ViewerComponent
                        ref={viewerRef}
                        type={lastSelectedItem?.type}
                        src={lastSelectedItem?.type === "pdf"
                            ? lastSelectedItem?.blobUrl
                            : lastSelectedItem?.dataUrl}
                        name={lastSelectedItem?.name}
                        onCropDone={async (croppedDataUrl) => {
                            setActiveAction(null);
                            await saveCroppedImage(croppedDataUrl);
                        }}
                    />
                )}
            </div>
        </div>
    );
}

const styles = {
    root: {
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "row",
        background: "#ffffff",
        overflow: "hidden",
    },
    sidebar: {
        width: 52,
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 0",
        background: "#f4f4f5",
        borderRight: "1px solid #e4e4e7",
        boxSizing: "border-box",
    },
    sidebarGroup: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "6px",
    },
    content: {
        flex: "1 1 0%",
        height: "100%",
        overflow: "hidden",
    },
};